import React from "react";
import { calculateUsageSteps, dayAheadSteps, estimateUsageSteps, estimateUsageParallelSteps, missingMTCSteps, parseXmltoDBSteps, passthroughSteps, recalculateUsageSteps, sftpToBlobSteps, withdrawalSteps } from "../../../utils/constants";
import { IRunLog } from "../../../interfaces/ILogs";

interface ProgressTrackerProps {
  taskName: string;
  logs : IRunLog[];
}

const stepMappings: { [key: string]: { key: string; label: string }[] } = {
  CalculateUsage: calculateUsageSteps,
  Passthrough: passthroughSteps,
  ParseXmltoDB: parseXmltoDBSteps,
  RecalculatingEstimates: recalculateUsageSteps,
  EstimatingUsage: estimateUsageSteps,
  EstimatingUsageParallel: estimateUsageParallelSteps,
  SFTPtoBlob: sftpToBlobSteps,
  Withdrawal: withdrawalSteps,
  MissingMTC: missingMTCSteps,
  DayAheadParseCsv: dayAheadSteps
};

const ProgressTracker: React.FC<ProgressTrackerProps> = ({ taskName, logs }) => {
  let allSteps = stepMappings[taskName] || [];

  if (
    ['CalculateUsage', 'ParseXmltoDB'].includes(taskName) &&
    logs.some(p => p.Step === 'notifyNewVersion') &&
    !allSteps.some(s => s.key === 'notifyNewVersion')
  ) {
    allSteps.push({ key: 'notifyNewVersion', label: 'Notified Skybill' });
  }

  if (taskName === "ParseXmltoDB") {
    const hasDstChange = logs.some(p => p.Step === "dstChangeDetected");
    if (!hasDstChange) {
      allSteps = allSteps.filter(s => s.key !== "dstChangeDetected");
    }
  }

  let steps = allSteps.filter(step => {
    // Always include if it's not optional
    if (step.key !== 'notifyNewVersion') return true;

    // Include 'notifyNewVersion' only if it's in progress
    return logs.some(p => p.Step === step.key);
  });

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: '60px' }}>
      {steps.map((step, index) => {
        const stepData = logs.find((p) => p.Step === step.key);
        const isAvailable = !!stepData;
        const isSuccess = stepData?.Status === 'Success';
        const isFailed = stepData?.Status === 'Failed';
        const isLast = index === steps.length - 1;

        return (
          <div
            key={step.key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexGrow: isLast ? 0 : 1
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: isAvailable ? (isSuccess ? '#28a745' : isFailed ? '#dc3545' : '#ffc107') : '#e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  color: 'white'
                }}
                title={`${step.key}: ${stepData?.Status || 'not started'}`}
              >
                {isSuccess ? '✓' : isFailed ? 'X' : '!'}
              </div>
              {!isLast && (
                <div
                  style={{
                    flex: 1,
                    height: '2px',
                    backgroundColor: isAvailable ? (isSuccess ? '#28a745' : '#e0e0e0') : '#e0e0e0',
                  }}
                />
              )}
            </div>
            <div
              style={{
                fontSize: '10px',
                marginTop: '4px',
                textAlign: 'center',
                whiteSpace: 'pre-line',
                color: isAvailable ? (isSuccess ? '#28a745' : isFailed ? '#dc3545' : '#000') : '#666'
              }}
            >
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgressTracker;
